import React from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Divider, Dropdown, Menu, Tooltip } from 'antd';
import styled from 'styled-components/macro';
import { useTranslation } from '../../../../i18n/useTranslation';

import { EntityType } from '../../../../types.generated';

import { useEntityRegistry } from '../../../useEntityRegistry';
import { useEntityData } from '../../shared/EntityContext';
import useRemoveRelatedEntity from './useRemoveRelatedEntity';

import { ReactComponent as MoreAction } from '../../../../images/more-vert.svg';
import GlossaryTermSkeleton from './GlossaryTermSkeleton';
import { PreviewType } from '../../Entity';

const Profile = styled.div`
    display: flex;
    padding: 24px;
`;

const BrowseDropDown = styled(Dropdown)`
    display: flex;
    cursor: pointer;
    align-items: center;
`;

const MenuItem = styled.div`
    padding: 0 4px;
    color: #262626;
`;

interface Props {
    urn: string;
    isLoading?: boolean;
    isInverse?: boolean;
    entityType: EntityType;
    relationshipType?: string;
    customRelationTypeUrn?: string;
    canRemove?: boolean;
    removeDisabledTooltip?: string;
    previewData?: any;
}

function LinkedEntity(props: Props) {
    const { t } = useTranslation();
    const {
        urn,
        relationshipType,
        entityType,
        isLoading,
        customRelationTypeUrn,
        isInverse,
        canRemove = true,
        removeDisabledTooltip,
        previewData,
    } = props;

    const entityRegistry = useEntityRegistry();
    const { entityData } = useEntityData();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = entityData?.readOnly?.readOnly ?? false;
    const isRemoveDisabled = !canRemove || isReadOnly;

    // Unauthorised takes precedence over read only when resolving the disabled tooltip.
    const getRemoveTooltip = () => {
        if (!canRemove) return removeDisabledTooltip;
        if (isReadOnly) return t('ASSET_MARKED_AS_READ_ONLY');
        return undefined;
    };

    const entityName = entityType === EntityType.GlossaryTerm ? 'Term' : 'Term Group';

    let displayName = '';

    if (previewData) {
        displayName = previewData.properties?.name || previewData.name;
    }

    const { onRemove } = useRemoveRelatedEntity(
        urn,
        isInverse || false,
        displayName,
        relationshipType || '',
        customRelationTypeUrn,
    );

    if (isLoading) return <GlossaryTermSkeleton />;

    const getPreviewProps = () => ({
        ...previewData,
        relationshipType,
    });

    return (
        <>
            <Profile>
                {entityRegistry.renderPreview(entityType, PreviewType.PREVIEW, getPreviewProps())}
                <BrowseDropDown
                    overlay={
                        <Menu>
                            <Tooltip title={getRemoveTooltip()} overlayClassName="f-tooltip-white">
                                <span>
                                    <Menu.Item
                                        key="0"
                                        disabled={isRemoveDisabled}
                                        aria-label={t('Remove {{entityName}}', { entityName })}
                                    >
                                        <MenuItem onClick={isRemoveDisabled ? undefined : onRemove}>
                                            <DeleteOutlined /> &nbsp; {t('Remove {{entityName}}', { entityName })}
                                        </MenuItem>
                                    </Menu.Item>
                                </span>
                            </Tooltip>
                        </Menu>
                    }
                    trigger={['click']}
                >
                    <Button
                        type="text"
                        tabIndex={0}
                        icon={<MoreAction />}
                        title={t('More Actions')}
                        className="icon-button ma-0"
                        aria-label={t('More Actions')}
                    />
                </BrowseDropDown>
            </Profile>
            <Divider className="ma-0" />
        </>
    );
}

export default LinkedEntity;
